'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/app/store';
import { fetchTenants, fetchUserTenants } from '@/app/store/thunks/tenantThunks';
import { setActiveTenant } from '@/app/store/slices/tenantSlice';
import { workspaceHref } from '@/app/features/workspace/utils/workspaceRoute';
import { useLocationPathname } from '@/app/features/workspace/routing/useWorkspacePath';

/**
 * Automatically loads tenants into Redux state when the user is authenticated.
 * Also restores the active tenant from user preferences (DB-persisted).
 * For white-label mode: auto-selects single tenant and redirects to /workspace.
 * Renders nothing — side-effect only.
 */
export const TenantLoader: React.FC = () => {
  const router = useRouter();
  // Live URL (static-export safe). Spec: workspace-static-spa-routing.
  const pathname = useLocationPathname();
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector((s: RootState) => s.auth.isAuthenticated);
  const tenants = useSelector((s: RootState) => s.tenant.tenants);
  const userTenants = useSelector((s: RootState) => s.tenant.userTenants);
  const forceWorkspaceHome = useSelector((s: RootState) => s.tenant.forceWorkspaceHome);
  const activeTenant = useSelector((s: RootState) => s.tenant.activeTenant);
  const preferences = useSelector((s: RootState) => s.userPreferences.preferences);
  const currentUserIdStr = useSelector((s: RootState) => s.auth.user?.id);
  const loadedRef = useRef(false);
  const restoredRef = useRef(false);
  const forceHomeHandledRef = useRef(false);

  // Reset refs when user changes (account switch) so tenants re-fetch for new user
  const prevUserIdRef = useRef(currentUserIdStr);
  useEffect(() => {
    if (currentUserIdStr !== prevUserIdRef.current) {
      prevUserIdRef.current = currentUserIdStr;
      loadedRef.current = false;
      restoredRef.current = false;
      forceHomeHandledRef.current = false;
    }
  }, [currentUserIdStr]);

  // Load tenants on login
  useEffect(() => {
    if (isAuthenticated && !loadedRef.current && tenants.length === 0) {
      loadedRef.current = true;
      dispatch(fetchTenants(false));
      // Also fetch user-scoped count for nav bar visibility
      const userId = currentUserIdStr ? parseInt(currentUserIdStr, 10) : null;
      if (userId && !isNaN(userId)) {
        dispatch(fetchUserTenants(userId));
      }
    }
    if (!isAuthenticated) {
      loadedRef.current = false;
      restoredRef.current = false;
      forceHomeHandledRef.current = false;
    }
  }, [isAuthenticated, dispatch, currentUserIdStr]);

  // Active-tenant restoration — race-free, single-direction.
  //
  // Precedence (strictest first):
  //   1. URL `/workspace/{code}` — wins on workspace routes. The workspace
  //      layout's URL-reconcile already handles it; TenantLoader stays out.
  //   2. Existing Redux value — populated by the tenantSlice initializer from
  //      localStorage, or by a prior dispatch in the same session. This is
  //      the user's last-known choice; trust it.
  //   3. DB preferences `active_tenant_id` — fallback only when Redux is
  //      truly empty (fresh browser, cleared cache, post-logout).
  //
  // Why each rule matters:
  //   - Rule 1 prevents F5-on-/workspace/TLT from flipping to a stale DB
  //     value (e.g. persist-listener debounce hadn't completed) and
  //     stranding the user on a spinner that times out to /main.
  //   - Rule 2 prevents the workspaceSlice matcher from clearing the
  //     just-fetched branding via a no-op restore (the matcher runs on every
  //     setActiveTenant; a redundant dispatch wipes company name + logos
  //     and the apps-fetch effect won't re-run because nothing structural
  //     changed).
  //   - Rule 3 handles the genuine "empty Redux" case (first visit on a new
  //     device, just logged in).
  //
  // The persist listener keeps DB in sync with Redux, so DB lagging Redux is
  // expected and benign — Redux is the live truth, DB is the across-session
  // cache.
  useEffect(() => {
    if (
      !isAuthenticated ||
      restoredRef.current ||
      tenants.length === 0 ||
      !preferences  // wait until preferences are loaded
    ) {
      return;
    }

    // Rule 1: workspace route → URL is authoritative, defer to layout.
    if (pathname && /^\/workspace\//i.test(pathname)) {
      restoredRef.current = true;
      return;
    }

    // Rule 2: Redux already has a value → trust it.
    if (activeTenant !== null) {
      restoredRef.current = true;
      return;
    }

    // Rule 3: Redux empty → restore from DB.
    restoredRef.current = true;
    const targetId = preferences.activeTenantId
      ? Number(preferences.activeTenantId)
      : null;
    if (targetId === null) {
      // DB has no saved tenant either. Nothing to restore.
      return;
    }
    const match = tenants.find((t) => t.id === targetId);
    if (match) {
      dispatch(setActiveTenant({
        tenantId: String(match.id),
        tenantCode: match.tenantCode,
        tenantName: match.tenantName,
        configurationJson: match.configurationJson ?? null,
      }));
    }
    // Saved id no longer exists in the user's membership — leave Redux null.
    // The caller's route (/main, /settings, etc.) will surface a picker if
    // needed; we don't dispatch setActiveTenant(null) here because that would
    // fire the matcher chain for no benefit (Redux was already null).
  }, [isAuthenticated, tenants, preferences, activeTenant, pathname, dispatch]);

  // White-label auto-select: when forceWorkspaceHome is true, user has exactly
  // 1 tenant, and no active tenant is set → auto-select and redirect to /workspace.
  // Handles the account-add/switch flow where resolvePostLoginRoute doesn't run.
  useEffect(() => {
    if (
      !isAuthenticated ||
      forceHomeHandledRef.current ||
      !forceWorkspaceHome ||
      activeTenant ||
      userTenants.length !== 1
    ) {
      return;
    }

    forceHomeHandledRef.current = true;
    const tenant = userTenants[0];
    dispatch(setActiveTenant({
      tenantId: String(tenant.id),
      tenantCode: tenant.tenantCode,
      tenantName: tenant.tenantName,
      configurationJson: tenant.configurationJson ?? null,
    }));
    router.push(workspaceHref(tenant));
  }, [isAuthenticated, forceWorkspaceHome, activeTenant, userTenants, dispatch, router]);

  // Route guard: if forceWorkspaceHome is true and user is on a restricted page
  // (/ or /main), redirect to the active tenant's workspace. Covers
  // account-switch while on /main, direct URL access, and other edge cases.
  useEffect(() => {
    if (
      !isAuthenticated ||
      !forceWorkspaceHome ||
      !activeTenant ||
      (pathname !== '/' && pathname !== '/main')
    ) {
      return;
    }
    router.replace(workspaceHref(activeTenant));
  }, [isAuthenticated, forceWorkspaceHome, activeTenant, pathname, router]);

  return null;
};

export default TenantLoader;
